import React, { useRef, useEffect, useState, type Dispatch, type SetStateAction } from 'react';
import { StyledProjectModal, StyledPaginationContainer } from './style';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Mousewheel, Navigation, Pagination } from 'swiper/modules';
import { Button, Spinner, Tooltip, Image, Label, Pagination as UIPagination } from 'ui';
import { Tag } from 'antd';
import type { ProjectType } from '../../Sidebars/Project';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useIntl } from 'react-intl';
import { useUserState } from 'contexts/UserContext';
import useQueryApiClient from 'utils/useQueryApiClient';
import { getMapModalPosition } from 'utils/getMapModalPosition';
import { useProjectDispatch, useProjectState } from 'contexts/ProjectContext';
import { UserGroups } from 'constants/userGroups';
import VoteButton, { type VotingProject } from 'components/VoteButton';
import type { NavigateOptions } from 'react-router/dist/lib/context';
import { type State as OpenedTypeState, useOpenedTypeState } from 'contexts/OpenedTypeContext';

type ModalTypeType = 'idea' | 'project';

interface ProjectModalType {
  onClose?: (e: React.MouseEvent<HTMLElement>) => void;
  isOpenProjectModal?: boolean;
  project: ProjectType | null;
  setProject: Dispatch<SetStateAction<ProjectType | null>>;
  setIsOpenProjectModal: Function;
  isProjectLoading: boolean;
  type?: ModalTypeType;
}

const ProjectModal = ({
  project,
  setProject,
  isOpenProjectModal,
  setIsOpenProjectModal,
  isProjectLoading,
  type = 'project',
}: ProjectModalType) => {
  let [searchParams] = useSearchParams();
  const projectList: Array<number> = JSON.parse(
    !!searchParams.get('geoProjectIdList') ? searchParams.get('geoProjectIdList') ?? '[]' : '[]'
  );

  const [isOverflowed, setIsOverflowed] = useState<boolean>(false);

  const intl = useIntl();
  const modalRef = useRef<HTMLDivElement>(null);
  const textRef = useRef<any>(null);
  const user = useUserState();
  const dispatch = useProjectDispatch();
  const { projects } = useProjectState();
  const navigate = useNavigate();
  const openedTypeState = useOpenedTypeState();
  const { appendData: submitVote, isLoading: isLoadingVotion } = useQueryApiClient({
    request: {
      url: 'api/v1/tapis/vote-for-project',
      method: 'POST',
    },
    onSuccess(_, project) {
      onVoted(project);
    },
  });

  const activeRole = user.roles.find((e) => e.id === user.selectedRole)?.code;
  const coordinatesForModal = project?.coordinatesForModal;
  const modalRefWidth = modalRef.current?.clientWidth ? modalRef.current.clientWidth : 0;
  const modalRefHeight = modalRef.current?.clientHeight ? modalRef.current.clientHeight : 0;
  const { left, top } = coordinatesForModal
    ? getMapModalPosition(
        coordinatesForModal.x,
        coordinatesForModal.y,
        coordinatesForModal.mapWidth,
        coordinatesForModal.mapHeight,
        modalRefWidth,
        modalRefHeight
      )
    : { left: 0, top: 0 };

  const projectCount = projectList.length;
  const [currentProject, setCurrentProject] = useState<number>(0);

  useEffect(() => {
    const checkModal = (event: Event): void => {
      if (
        (event.target as Node).nodeName !== 'CANVAS' &&
        modalRef.current &&
        !modalRef.current.contains(event.target as Node)
      ) {
        setIsOpenProjectModal(false);
      }
    };
    window.addEventListener('click', checkModal);

    return () => {
      window.removeEventListener('click', checkModal);
    };
  }, []);

  useEffect(() => {
    const { current } = textRef;

    if (current) {
      setIsOverflowed(current?.scrollHeight > current?.clientHeight);
    }
  }, [project]);

  const onVoted = (project: VotingProject | null) => {
    if (project) {
      dispatch({ type: 'VOTE_PROJECT', payload: project?.id });
    }
  };

  const appendVote = (project: VotingProject | null) => {
    if (!project) {
      return;
    }

    if (activeRole === UserGroups.proxy) {
      return onVoted(project);
    }

    submitVote(
      {
        vote: {
          project_id: project?.id,
          is_active: !project?.has_voted,
        },
      },
      undefined,
      project
    );
  };

  const handleOpenProjectView = (type: ModalTypeType, id?: number) => {
    const navigationMap = getNavMap(id, openedTypeState, project?.atvk_id);

    navigate(navigationMap[type].url, navigationMap[type].options);
    setIsOpenProjectModal(false);
  };

  const renderVoteButton = () => {
    if (project?.state === 'in_voting') {
      return (
        <VoteButton
          project={projects.find(({ id }) => id === project.id)}
          button={{ loading: isLoadingVotion }}
          handleVote={appendVote}
          tooltip={{ placement: 'top', overlayClassName: 'white' }}
        />
      );
    }

    return (
      <Tag className={`status ${project?.state} ${type}`}>
        {intl.formatMessage({ id: `participation_budget.${project?.state}` })}
      </Tag>
    );
  };

  const renderImages = () => {
    const { pictures = [], versions: [{ pictures: versionPictures = [] }] = [{}] } = project || {};
    const images = typeof pictures[0] === 'string' ? pictures : versionPictures.map(({ url }) => url);

    return (
      <Swiper
        cssMode={true}
        navigation={true}
        pagination={true}
        mousewheel={true}
        loop={true}
        modules={[Navigation, Pagination, Mousewheel]}
        className="project-item__swiper"
      >
        {images.length ? (
          images.map((image: string, idx: number) => (
            <SwiperSlide key={idx}>
              <Image src={image} type={type === 'idea' ? 'idea' : undefined} size="medium" />
            </SwiperSlide>
          ))
        ) : (
          <SwiperSlide>
            <Image type={type === 'idea' ? 'idea' : undefined} size="medium" />
          </SwiperSlide>
        )}
      </Swiper>
    );
  };

  return (
    <StyledProjectModal>
      <div
        ref={modalRef}
        className={`modal ${isOpenProjectModal ? 'open' : ''}`}
        style={
          coordinatesForModal
            ? {
                left: `${left}px`,
                top: `${top}px`,
              }
            : {}
        }
      >
        <Spinner spinning={isProjectLoading}>
          {!!projectList?.length && (
            <StyledPaginationContainer>
              <UIPagination
                simple={{ readOnly: true }}
                compact={true}
                current={currentProject}
                pageSize={1}
                total={projectCount}
                onChange={(new_project) => {
                  setCurrentProject(new_project);
                  setProject((old) => ({
                    ...old,
                    ...projects.find((p) => p.id === projectList[new_project - 1]),
                  }));
                }}
              />
            </StyledPaginationContainer>
          )}
          {renderImages()}
          <div className="project-item__content-wrapper">
            <Tooltip overlayClassName="white" title={isOverflowed ? project?.name : ''}>
              <h2 className="title_project_modal" ref={textRef}>
                {project?.name}
              </h2>
            </Tooltip>
          </div>
          {!isProjectLoading && (
            <div className="buttons_group">
              <div className="left_side">{renderVoteButton()}</div>
              <Button
                label={intl.formatMessage({
                  id: 'participation_budget.projects_see_description',
                })}
                type="link"
                onClick={() => handleOpenProjectView(type, project?.id)}
              />
            </div>
          )}
        </Spinner>
      </div>
    </StyledProjectModal>
  );
};

export const getNavMap = (
  id?: number,
  openedTypeState?: OpenedTypeState,
  atvkId?: string
): { [key in ModalTypeType]: { url: string; options: NavigateOptions } } => ({
  idea: {
    url: `/main?idea-view=open&side=right&geoIdeaId=${id}`,
    options: { state: { backBtnRoute: '/main?my-participation=open&tab=ideas_tab' } },
  },
  project: {
    url: `/main?project-view=open&side=left&geoProjectId=${id}`,
    options: {
      state: {
        backBtnRoute: openedTypeState?.openMunicipalProject
          ? `/main?municipal-project=open&atvk_id=${atvkId}`
          : '/main?participation-budget=open',
      },
    },
  },
});

export default ProjectModal;
